/**
 * Deep thinking, as three rooms.
 *
 * Nine engines answer three questions people actually ask — what should we
 * do, when can we do it, and can we trust the numbers — so the nine screens
 * are shown as three rooms of three tabs. Every engine and every figure is
 * where it was; a tab is the screen it always was, and the old addresses
 * open the right tab.
 */
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { RoomContext } from "../../layout/roomContext";
import { ProgrammePage } from "../programme/ProgrammePage";
import { CounterfactualPage } from "../counterfactual/CounterfactualPage";
import { LiabilityPage } from "../liability/LiabilityPage";
import { PlanningPage } from "../planning/PlanningPage";
import { WeatherPage } from "../weather/WeatherPage";
import { CapacityPage } from "../capacity/CapacityPage";
import { DriftPage } from "../drift/DriftPage";
import { LedgerPage } from "../ledger/LedgerPage";
import { ReliabilityPage } from "../reliability/ReliabilityPage";
import { t } from "../../i18n";
import r from "./Think.module.css";

export interface RoomTab {
  key: string;
  label: string;
  /** the question the tab answers, in plain words */
  asks: string;
  render: () => ReactNode;
}

export interface RoomDef {
  route: string;
  title: string;
  asks: string;
  tabs: RoomTab[];
}

export const ROOMS: Record<"decide" | "window" | "trust", RoomDef> = {
  decide: {
    route: "/decide",
    title: "Decide the program",
    asks: "What should we do with this year's money, and what does waiting cost?",
    tabs: [
      { key: "plan", label: "The plan", asks: "What to do, when — and what nobody can decide yet", render: () => <ProgrammePage /> },
      { key: "waiting", label: "Cost of waiting", asks: "What slips at each budget, and what deferring it adds", render: () => <LiabilityPage /> },
      { key: "change", label: "Try a change", asks: "What moves with more money or more crews — and what does not", render: () => <CounterfactualPage /> },
    ],
  },
  window: {
    route: "/window",
    title: "Find a window",
    asks: "When can the work happen — with traffic, weather and the stands all allowing it?",
    tabs: [
      { key: "when", label: "When to work", asks: "A window that traffic and weather both allow", render: () => <PlanningPage /> },
      { key: "weather", label: "Weather", asks: "Whether the work can go ahead at a given hour", render: () => <WeatherPage /> },
      { key: "closure", label: "Closing a stand", asks: "What closing a stand costs, and where the flights would go", render: () => <CapacityPage /> },
    ],
  },
  trust: {
    route: "/trust",
    title: "Can we trust it",
    asks: "Are the forecasts still right, do our records agree, and how do things really fail?",
    tabs: [
      { key: "models", label: "The forecasts", asks: "Whether each predictive model still describes the estate", render: () => <DriftPage /> },
      { key: "records", label: "Disagreements", asks: "Where two parties' records of the same fact diverge", render: () => <LedgerPage /> },
      { key: "failures", label: "How things fail", asks: "How each population of assets actually fails", render: () => <ReliabilityPage /> },
    ],
  },
};

/** The old address of each screen, and the tab it now lives in. */
export const MOVED: Record<string, string> = {
  "/programme": "/decide?tab=plan",
  "/liability": "/decide?tab=waiting",
  "/what-if": "/decide?tab=change",
  "/planning": "/window?tab=when",
  "/weather": "/window?tab=weather",
  "/capacity": "/window?tab=closure",
  "/drift": "/trust?tab=models",
  "/ledger": "/trust?tab=records",
  "/reliability": "/trust?tab=failures",
};

function Room({ def }: { def: RoomDef }) {
  const [params, setParams] = useSearchParams();
  const tab = def.tabs.find((x) => x.key === params.get("tab")) ?? def.tabs[0];
  const bar = (
    <header className={r.roomBar} data-testid="room-bar">
      <div className={r.roomHead}>
        <div>
          <div className="eyebrow">{t("Deep thinking")}</div>
          <h1>{t(def.title)}</h1>
          <p className={r.roomAsks}>{t(def.asks)}</p>
        </div>
        <Link to="/think" className={r.back}>
          ✦ {t("Ask a question")}
        </Link>
      </div>
      <nav className={r.tabs} aria-label={t(def.title)}>
        {def.tabs.map((x, i) => (
          <button
            key={x.key}
            type="button"
            className={r.tab}
            aria-current={x.key === tab.key ? "page" : undefined}
            onClick={() => setParams({ tab: x.key }, { replace: true })}
            data-testid={`room-tab-${x.key}`}
          >
            <span className={r.tabNo}>{i + 1}</span>
            <span>
              <b>{t(x.label)}</b>
              <small>{t(x.asks)}</small>
            </span>
          </button>
        ))}
      </nav>
    </header>
  );
  return <RoomContext.Provider value={bar}>{tab.render()}</RoomContext.Provider>;
}

export const DecideRoom = () => <Room def={ROOMS.decide} />;
export const WindowRoom = () => <Room def={ROOMS.window} />;
export const TrustRoom = () => <Room def={ROOMS.trust} />;
